<template>
  <div class="header" :class="headerClasses">
    <i v-if="hasPermission('loading', 'change')" class="i-util-bars schedule-item-handle">
    </i>

    <div class="title" :title="title" @click="toggle">
      {{ title.length > 13 ? title.substring(0, 13) + '...' : title }}
    </div>

    <div class="groups-title">{{ 'base.groups' | translate }}</div>
    <div class="groups-short">{{ groupsListLabel(groups) }}</div>
    
    <div v-if="item.is_offline">
      <svg 
        class="icon-offline-mode-icon"
        @mouseenter="highlightRelatedItem"
        @mouseleave="unhighlightRelatedItem"
      >
        <use href="/svg/sprite.svg#icon-offline-mode"></use>
      </svg>
    </div>
    <div v-else>
      <div :title="$t('base.leftovers')" class="leftovers-mark">
        <div v-if="item.is_adjustment_leftovers">
          <svg class="leftovers-mark-icon">
            <use href="/svg/sprite.svg#icon-leftovers"></use>
          </svg>
        </div>
      </div>
      <div class="a-status">
        <div v-if="item.auto_load">A</div>
      </div>
    </div>

    <div class="status-label">
      <status-label :status="status" />
    </div>

    <ui-number-input ref="mainCoefficient" postfix=" %" :min="1" :blocked="locked" :decimalPlaces="1" :reactive="false"
      :value="displayCoefficient" @input="onCoefficientChange" />
    <div class="controls-wrapper">
      <ration-settings-dropdown v-if="hasPermission('loading', 'change')" :item="item" @updated="onChange" @on-copy="openCreateRationCopy"
        @on-load="openScheduleLoadDialog" @on-upload="openScheduleUploadDialog" @on-close="closeRationInfoDialog" />
      <v-btn icon>
        <v-chevron v-model="toggled" />
      </v-btn>
    </div>

    <!-- Schedule loading -->
    <v-dialog max-width="500" v-model="showScheduleLoadDialog">
      <schedule-load-dialog :item="item" @update="$emit('updated')" @on-close="closeScheduleLoadDialog" />
    </v-dialog>

    <!-- Schedule uploading -->
    <v-dialog max-width="500" v-model="showScheduleUploadDialog">
      <schedule-upload-dialog :item="item" @update="$emit('updated')" @on-close="closeScheduleUploadDialog" />
    </v-dialog>

    <!-- Create copy dialog -->
    <v-dialog max-width="350" v-model="modals.showCreateRationCopy">
      <create-ration-copy-modal :ration-id="item.id" @update="onChange" @close="closeCreateRationCopy" />
    </v-dialog>
  </div>
</template>

<script>
import api from '@/api'

import UiNumberInput from '@/components/ui/ui-number-input/UiNumberInput.vue'

import RationSettingsDropdown from '../dialogs/RationSettingsDropdown'
import CreateRationCopyModal from '../dialogs/CreateRationCopyModal'

import ScheduleLoadDialog from '../dialogs/ScheduleLoadDialog.vue'
import ScheduleUploadDialog from '../dialogs/ScheduleUploadDialog.vue'

import StatusLabel from './StatusLabel.vue'

export default {
  name: 'ItemHeader',

  components: {
    UiNumberInput,
    RationSettingsDropdown,
    CreateRationCopyModal,
    ScheduleLoadDialog,
    ScheduleUploadDialog,
    StatusLabel
  },

  props: {
    item: Object,
    title: String,
    groups: Array,
    status: String,
    locked: Boolean
  },

  data: () => ({
    modals: {
      showCreateRationCopy: false
    },
    toggled: false,
    showRationSettingsDialog: false,
    showScheduleLoadDialog: false,
    showScheduleUploadDialog: false,
    showCreateCopyDialog: false,
    initialCoefficient: 0,
    isMounted: false
  }),

  mounted () {
    this.initialCoefficient = this.item.coefficient

    this.$nextTick(() => {
      this.isMounted = true
    })
  },

  computed: {
    displayCoefficient () {
      return Math.round(this.item.coefficient * 10) / 10
    },

    headerClasses () {
      return {
        'header--is-highlighted': this.item._isHighlighted
      }
    }
  },

  methods: {
    groupsListLabel (items) {
      let str = ''
      if (items) {
        for (let i = 0; i < items.length; i++) {
          str += items[i].name
          if (i < items.length - 1) {
            str += ', '
          }
        }
      }
      return str.length > 22 ? str.substring(0, 23) + '...' : str
    },

    highlightRelatedItem () {
      if (this.item.offline_related) {
        this.$emit('highlight-related', this.item.offline_related)
      }
    },

    unhighlightRelatedItem () {
      if (this.item.offline_related) {
        this.$emit('unhighlight-related', this.item.offline_related)
      }
    },

    onCoefficientChange (value) {
      if (!this.isMounted) return

      const newCoefficient = parseFloat(value)
      const changed = Number.isFinite(newCoefficient) &&
        Math.round(newCoefficient * 10) !== Math.round(this.initialCoefficient * 10)

      if (changed) {
        const ratio = newCoefficient / this.initialCoefficient
        this.item.coefficient = newCoefficient

        // Update initial weights and recalculate base weights for all groups
        if (this.groups && this.groups.length > 0) {
          this.groups.forEach(group => {
            if (group.plan_weight && group.plan_weight > 0) {
              group.plan_weight = Math.round(group.plan_weight * ratio)

              // Recalculate base weight per animal for this group
              if (group.animal_count > 0 && group.coefficient > 0) {
                const newBaseWeight = (group.plan_weight * 100) / (group.animal_count * group.coefficient)
                // Update in parent's groupInitialWeights
                if (this.$parent && this.$parent.groupInitialWeights) {
                  this.$parent.groupInitialWeights[group.id] = newBaseWeight
                }
              }
            }
          })
        }
        this.initialCoefficient = newCoefficient
        this.update()
      }
    },

    // Ration info dialog
    openRationInfoDialog () {
      this.showRationSettingsDialog = true
    },

    closeRationInfoDialog () {
      this.showRationSettingsDialog = false
    },

    // Schedule load dialog
    openScheduleLoadDialog () {
      this.closeRationInfoDialog()
      this.showScheduleLoadDialog = true
    },

    closeScheduleLoadDialog () {
      this.showScheduleLoadDialog = false
    },

    // Schedule upload dialog
    openScheduleUploadDialog () {
      this.closeRationInfoDialog()
      this.showScheduleUploadDialog = true
    },

    closeScheduleUploadDialog () {
      this.showScheduleUploadDialog = false
    },

    // Schedule copy dialog
    openCreateRationCopy () {
      this.modals.showCreateRationCopy = true
    },

    closeCreateRationCopy () {
      this.modals.showCreateRationCopy = false
    },

    async update () {
      try {
        await api.schedule.updateById(this.item.id, this.item)
        this.$toasted.show(this.$t('info.changed'))
        this.onChange()
      } catch (error) {
        console.error('Update failed:', error)
        this.$toasted.error(this.$t('info.error_occurred'))
      }
    },

    toggle () {
      this.toggled = !this.toggled
    },

    onChange () {
      this.$emit('updated')
    }
  },

  watch: {
    item (newItem) {
      this.initialCoefficient = newItem.coefficient
    },

    toggled () {
      this.$emit('on-toggle', this.toggled)
    }
  }
}
</script>

<style lang="scss" scoped>
.schedule-item-handle {
  cursor: move;
  z-index: 2;
}

.header {
  position: relative;
  height: 45px;
  padding-left: 1.5rem;
  padding-right: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: background-color 0.3s ease;

  &--is-highlighted {
    background-color: #fff3cd;
    // border-left: 3px solid #ff9800;
  }
}

.header i {
  margin-right: 2rem;
}

.header span {
  cursor: pointer;
}

.header .title {
  cursor: pointer;
  width: 22%;
  font-size: 1.7rem;
}

.header .groups-title {
  font-size: 1.4rem;
  font-weight: 300;
  margin-right: 1rem;
}

.header .groups-short {
  width: 20%;
  font-size: 1.5rem;
  margin-right: 1rem;
}

.header .a-status {
  width: 30px;
  font-size: 15px;
  margin-left: -20px;
  text-align: center;
  padding-right: 40px;
}

.header .status-label {
  width: 15%;
  margin-right: 1rem;
}

.header .controls-wrapper {
  display: flex;
  align-items: center;
  margin-left: 2.5rem;
}

.leftovers-mark {
  margin-right: 10px;
  font-size: 15px;
  width: 10px;
}

.leftovers-mark-icon {
  width: 40px;
  height: 25px;
  transform: scale(0.8);
  margin-right: 1.1rem;
}

.icon-offline-mode-icon {
  width: 40px;
  height: 40px;
  transform: scale(0.8);
  margin-right: 0rem;
  margin-left: -2rem;
  margin-top: 0.75rem;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }
}
</style>
